import { ExecutingSidePanel } from '@/@types/content';
import Panel, { PanelProps } from '../../Panel';
import { templateString } from '@/utils/helpers';
import PanelContentWithErrorBoundary from '@/components/blocks/ErrorFallback/PanelContentWithErrorBoundary';
import ContentArea from '@/components/cms/ContentArea';
import { useEffect, useMemo, useState } from 'react';
import Button from '../../Buttons/Button';
import Icon from '../../Icon';
import LoadingDots from '../../Buttons/LoadingDots';
import classnames from 'classnames';
import Text from '@/components/cms/Text';
import { expandContent } from '@/lib/helpers/expandChildren';

export interface ExecutingCreditPlanProps extends Omit<PanelProps, 'size' | 'children'> {
  content?: ExecutingSidePanel;
  isComplete?: boolean;
  onBackButtonPress?: () => void;
  loadingStepIndex?: number;
  onAdvanceLoadingStepIndex?: (stepCount: number) => void;
}

const FEEDBACK_STEP_INTERVAL_MS = 60_000;

const ExecutingCreditPlanSidePanel: React.FC<ExecutingCreditPlanProps> = ({
  isComplete,
  onBackButtonPress,
  loadingStepIndex,
  onAdvanceLoadingStepIndex,
  content: {
    heading: rawHeading,
    subheading,
    buttonText,
    mainContent,
    feedbackLoadingSteps = []
  } = {},
  ...panelProps
}) => {
  const [internalStepIndex, setInternalStepIndex] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const activeStepIndex = loadingStepIndex ?? internalStepIndex;

  const messageInterval = useMemo(() => {
    if (!feedbackLoadingSteps?.length) return 0;

    const val = FEEDBACK_STEP_INTERVAL_MS / feedbackLoadingSteps?.length;

    return val <= 0 ? 0 : val;
  }, [feedbackLoadingSteps]);

  useEffect(() => {
    setIsPanelOpen(!!panelProps?.isOpen);
  }, [panelProps?.isOpen]);

  useEffect(() => {
    if (messageInterval > 0) {
      const stepsCount = feedbackLoadingSteps.length;
      if (stepsCount === 0 || !panelProps?.isOpen) return;

      const intervalId = setInterval(() => {
        if (isComplete || activeStepIndex >= stepsCount - 1) return;

        if (onAdvanceLoadingStepIndex) {
          onAdvanceLoadingStepIndex(stepsCount);
          return;
        }

        setInternalStepIndex(prev => {
          const newVal = prev + 1;
          if (newVal >= stepsCount) return stepsCount - 1;
          return newVal;
        });
      }, messageInterval);

      return () => clearInterval(intervalId);
    }
  }, [
    activeStepIndex,
    feedbackLoadingSteps.length,
    isComplete,
    messageInterval,
    onAdvanceLoadingStepIndex,
    panelProps?.isOpen
  ]);

  const renderFeedbackMessages = useMemo(() => {
    return (
      feedbackLoadingSteps?.length > 0 && (
        <ul className="mt-4 space-y-2">
          {feedbackLoadingSteps.map((message, index) => {
            const isLast = index === feedbackLoadingSteps.length - 1;
            const isActive = index === activeStepIndex;
            const classes = classnames('!mt-0 flex flex-row items-center font-medium', {
              'font-medium': isActive,
              'text-gray-dark': !isActive,
              hidden: activeStepIndex < index,
              '!mb-4': !isLast
            });

            return (
              <li key={`feedback-message-${index}`} data-active={isActive} className={classes}>
                <span className="mr-4">
                  {isActive ? (
                    <LoadingDots color="gradient" dotSize="small" />
                  ) : (
                    <Icon name="checkmark" size="large" />
                  )}
                </span>
                <Text
                  element="p"
                  className={classnames({
                    'animate-fadeIn': isActive
                  })}
                >
                  {message}
                </Text>
              </li>
            );
          })}
        </ul>
      )
    );
  }, [activeStepIndex, feedbackLoadingSteps]);

  return (
    <Panel
      {...{
        ...panelProps,
        isOpen: isPanelOpen
      }}
      size="medium"
    >
      <Panel.Content onBackButtonPress={onBackButtonPress}>
        <Panel.Header>
          <div>
            <h2 className="heading-2">{rawHeading}</h2>
            {subheading && (
              <p className="text-2 mt-6 font-normal">{templateString(subheading, {}, true)}</p>
            )}
          </div>
        </Panel.Header>
        <PanelContentWithErrorBoundary>
          <Panel.Body>
            <>
              <ContentArea
                components={expandContent(mainContent)}
                propertyName="MainContent"
                className="mb-6"
              />
              {renderFeedbackMessages}
            </>
          </Panel.Body>
        </PanelContentWithErrorBoundary>
        <Panel.Footer divider="line" className="flex justify-end gap-5">
          <Button
            size="large"
            label={buttonText}
            onClick={() => {
              if (panelProps?.onOpenChange) panelProps.onOpenChange(false);

              setIsPanelOpen(false);
            }}
          />
        </Panel.Footer>
      </Panel.Content>
    </Panel>
  );
};

export default ExecutingCreditPlanSidePanel;
